import React, { useMemo } from 'react';
import { Property, AppUser } from '../../types';
import { SpinnerIcon } from '../Icons';
import { PropertyCard } from './PropertyCard';
import { PropertyCardSkeleton } from './PropertyCardSkeleton';
import { useConnection } from '../../context/ConnectionContext';
import { usePropertyCommentCounts } from '../../hooks/usePropertyCommentCounts';

interface PropertiesTableProps {
  properties: (Property & { sourceCollection: 'ciencuadras' | 'aliados' | 'brokers' })[];
  isLoading: boolean;
  selectedPropertiesForCsv: string[];
  totalMatchingPropertiesCount: number;
  isProcessingSelectAll: boolean;
  onViewProperty: (property: Property) => void;
  onShareProperty: (property: Property) => void;
  onToggleSelectPropertyForCsv: (propertyId: string) => void;
  onToggleSelectAllForCsv: () => void;
  fetchPropertiesError: string | null;
  appUser: AppUser | null;
  onInterested: (property: Property) => void;
  isSelectionModeActive?: boolean;
  inmobiliariasConfig?: Record<string, any>;
  isPropertySelectedFn?: (propertyId: string) => boolean;
  isAllSelected?: boolean;
  selectedCount?: number;
}

export const PropertiesTable: React.FC<PropertiesTableProps> = React.memo(({
  properties,
  isLoading,
  selectedPropertiesForCsv,
  totalMatchingPropertiesCount,
  isProcessingSelectAll,
  onViewProperty,
  onShareProperty,
  onToggleSelectPropertyForCsv,
  onToggleSelectAllForCsv,
  fetchPropertiesError,
  appUser,
  onInterested,
  isSelectionModeActive = false,
  inmobiliariasConfig = {},
  isPropertySelectedFn,
  isAllSelected,
  selectedCount
}) => {
  const { isOffline } = useConnection();
  const propertyCodes = useMemo(() => properties.map((p) => p.codigo), [properties]);
  const { getCount } = usePropertyCommentCounts(propertyCodes, appUser);

  const allFilterMatchedSelected = isAllSelected !== undefined 
    ? isAllSelected 
    : (totalMatchingPropertiesCount > 0 && selectedPropertiesForCsv.length === totalMatchingPropertiesCount);
  const displaySelectedCount = selectedCount !== undefined ? selectedCount : selectedPropertiesForCsv.length;

  if (isLoading && properties.length === 0 && !fetchPropertiesError && !isProcessingSelectAll) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 lg:gap-5 items-stretch">
        {[...Array(8)].map((_, i) => <PropertyCardSkeleton key={i} isCompact={true} />)}
      </div>
    );
  }

  return (
    <div>
        {isSelectionModeActive && (
            <div className={`mb-6 flex items-center p-6 rounded-2xl border-2 transition-all duration-300 shadow-lg ${allFilterMatchedSelected ? 'bg-green-100 border-green-500 ring-4 ring-green-200' : 'bg-sky-100 border-sky-300'}`}>
                <div className="relative flex items-center">
                    <input
                        id="select-all-properties"
                        type="checkbox"
                        className="form-checkbox h-12 w-12 text-sky-600 border-gray-400 rounded-xl focus:ring-sky-500 disabled:opacity-50 transition-all cursor-pointer shadow-sm"
                        checked={allFilterMatchedSelected}
                        onChange={onToggleSelectAllForCsv}
                        aria-label="Seleccionar todos"
                        disabled={isLoading || totalMatchingPropertiesCount === 0 || isProcessingSelectAll || isOffline}
                    />
                    {isProcessingSelectAll && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-xl">
                            <SpinnerIcon className="w-6 h-6 text-sky-600" />
                        </div>
                    )}
                </div>
                <label htmlFor="select-all-properties" className={`ml-6 text-xl md:text-2xl font-black cursor-pointer select-none transition-colors ${allFilterMatchedSelected ? 'text-green-900' : 'text-sky-900'}`}>
                    {allFilterMatchedSelected 
                        ? `¡Todos seleccionados! (${totalMatchingPropertiesCount} inmuebles)` 
                        : displaySelectedCount > 0 
                            ? `${displaySelectedCount} de ${totalMatchingPropertiesCount} seleccionados (Clic para seleccionar todos)`
                            : `Seleccionar todos los resultados (${totalMatchingPropertiesCount}) para exportar o compartir`
                    }
                </label>
            </div>
        )}

        {(isLoading && properties.length === 0 && !isProcessingSelectAll) || (isProcessingSelectAll && properties.length === 0) ? (
            <div className="px-4 py-20 text-center text-sm text-gray-500">
                <div className="flex flex-col justify-center items-center space-y-4">
                    <SpinnerIcon className="w-12 h-12 text-sky-600 animate-spin" /> 
                    <p className="text-lg font-bold text-sky-900 uppercase tracking-widest">
                        {isProcessingSelectAll ? 'Procesando selección masiva de todo el inventario...' : 'Cargando inmuebles...'}
                    </p>
                </div>
            </div>
        ) : properties.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 lg:gap-5 items-stretch">
                {properties.map(property => {
                    const isSelected = isPropertySelectedFn ? isPropertySelectedFn(property.id) : selectedPropertiesForCsv.includes(property.id);
                    return (
                        <div 
                            key={property.id} 
                            className="h-full" 
                            style={{ contentVisibility: 'auto', containIntrinsicSize: '300px 420px' }}
                        >
                            <PropertyCard
                                property={property}
                                isSelected={isSelected}
                                onView={() => onViewProperty(property)}
                                onShare={() => onShareProperty(property)}
                                onToggleSelect={() => onToggleSelectPropertyForCsv(property.id)}
                                disabled={isProcessingSelectAll || isOffline}
                                isCompact={true}
                                source={property.sourceCollection}
                                appUser={appUser}
                                onInterested={onInterested}
                                isSelectionModeActive={isSelectionModeActive}
                                inmobiliariasConfig={inmobiliariasConfig}
                                commentCount={getCount(property.codigo)}
                            />
                        </div>
                    );
                })}
            </div>
        ) : (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
                {fetchPropertiesError 
                    ? `Error: ${fetchPropertiesError.split(" Es posible que falte un índice")[0]}` 
                    : 'No se encontraron inmuebles.'
                }
            </div>
        )}
    </div>
  );
});